import { UiUtilities } from '@yiluhub/ui-sdk-react';
import { SearchItem } from '@yiluhub/yilu-amp-types';
import { YILU_FEATURE } from 'applicationConstants';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import tzlookup from 'tz-lookup';

import { yiluTracking } from 'utils/index';

import { useIsFeatureFlagEnabled } from 'context/EnterprisePartnerConfiguration';
import { usePaymentInputContext } from 'context/PaymentInput';
import { PaymentMethods } from 'context/PaymentInput/context';

import { ParkingProductSummaryProps } from 'modules/parking/components/ParkingProductSummary/types';
import { getParkingDetails } from 'modules/parking/utils/getParkingDetails';

export function useParkingProductSummaryProps(
  currentParking: SearchItem | undefined,
  paymentFormRef: React.RefObject<HTMLFormElement>,
) {
  const { t } = useTranslation();

  const isCreditCardDisabled = useIsFeatureFlagEnabled(YILU_FEATURE.DISABLE_CREDIT_CARD_PAYMENTS);
  const { currentPaymentMethod } = usePaymentInputContext();
  const isCreditCardPaymentMethod = currentPaymentMethod === PaymentMethods.CREDIT_CARD;

  const parkingProductSummaryProps = useMemo(() => {
    if (!currentParking) {
      return null;
    }

    const parkingDetails = getParkingDetails(currentParking);
    const { latitude, longitude } = parkingDetails;
    const timezone = latitude && longitude ? tzlookup(latitude, longitude) : undefined;

    const _props: ParkingProductSummaryProps = {
      price: parkingDetails.price,
      currency: parkingDetails.currency,
      enterDate: UiUtilities.formatLocalDate(parkingDetails.arrival, 'YYYY-MM-DD', timezone),
      enterTime: UiUtilities.formatLocalDate(parkingDetails.arrival, 'HH:mm', timezone),
      exitDate: UiUtilities.formatLocalDate(parkingDetails.departure, 'YYYY-MM-DD', timezone),
      exitTime: UiUtilities.formatLocalDate(parkingDetails.departure, 'HH:mm', timezone),
      parkingName: parkingDetails.name,
      termsDetails: {
        termsUrl: parkingDetails.policy.termsUrl,
        productDetails: {
          providerId: parkingDetails.providerId,
          locationName: parkingDetails.address,
        },
      },
      onClick() {
        if (paymentFormRef?.current) {
          const paymentForm = paymentFormRef.current;
          paymentForm.dispatchEvent(
            new Event('submit', {
              cancelable: true,
              bubbles: true,
            }),
          );

          yiluTracking.sendGAEvent({
            event: 'pip_cta_clicked',
            category: 'parking-pip',
            label: 'parking_pip_cta_clicked',
          });
        }
      },
      isDisabled: isCreditCardDisabled && isCreditCardPaymentMethod,
      submitBtnLabel: t('Pay now'),
      address: parkingDetails.address,
    };
    return _props;
  }, [paymentFormRef, currentParking, t, isCreditCardDisabled, isCreditCardPaymentMethod]);

  return parkingProductSummaryProps;
}
